import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import classnames from 'classnames';
import type { FC, ReactNode } from 'react';
import type { MessageDescriptor } from 'react-intl';
import styled from 'styled-components';

import { ButtonBase } from 'atoms/buttons/ButtonBase';
import { TagButton } from 'atoms/buttons/TagButton';
import { FontAwesomeIcon } from 'atoms/icons';
import { Vertical } from 'atoms/layout/flex';
import { BodyM } from 'atoms/typography';
import { useIntlContext } from 'contexts/intl';
import { useScreenSize } from 'hooks/device/useScreenSize';
import { useToggle } from 'hooks/useToggle';
import { laptopAndAbove } from 'style/mediaQuery';

export type Selection = [string, string | undefined, string | undefined];
type SubSection = Record<string, undefined | string[]>;
export type TreeSchema = Record<string, undefined | SubSection>;

type Props = {
  schema: TreeSchema;
  selected: Selection;
  onSelect: (selected: Selection) => void;
  title: ReactNode;
  children: ReactNode;
  translations?: Record<string, MessageDescriptor>;
};

const Root = styled(Vertical).attrs({ gap: 3 })`
  margin-top: var(--double-unit);
`;

const SideBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--unit);
  border-bottom: 1px solid var(--c-nd-100);
  padding-bottom: var(--double-unit);
  button {
    text-align: left;
  }
  @media ${laptopAndAbove} {
    min-width: 220px;
    border-bottom: none;
  }
`;
const Section = styled(Vertical).attrs({ gap: 0 })`
  justify-content: flex-start;
`;
const Sub1 = styled(Vertical)`
  display: none;
  margin-left: var(--double-unit);
  justify-content: flex-start;
  &.selected {
    display: flex;
  }
`;
const Selected = styled(ButtonBase)`
  display: flex;
  justify-content: space-between;
  color: var(--c-white);
`;
const Container = styled.div`
  @media ${laptopAndAbove} {
    display: flex;
  }
`;
const Content = styled(Vertical).attrs({ gap: 2 })`
  width: 100%;
`;
const Sub2 = styled.div`
  display: flex;
  white-space: nowrap;
  overflow: auto;
  gap: var(--unit);
`;

const Button: FC<
  React.PropsWithChildren<{ onClick: () => void; selected: boolean }>
> = ({ onClick, selected, children }) => (
  <BodyM
    color={selected ? 'var(--c-blue-400)' : 'var(--c-nd-600)'}
    as="button"
    onClick={onClick}
    type="button"
    bold={selected}
  >
    {children}
  </BodyM>
);

export const Tree = ({
  schema,
  selected,
  onSelect,
  title,
  translations,
  children,
}: Props) => {
  const { up: isLaptop } = useScreenSize('laptop');
  const [open, toggleOpen] = useToggle(false);
  const { formatMessage } = useIntlContext();
  const [selectedSection, selectedSub1, selectedSub2] = selected;

  const selectSub1 = (section: string, sub1?: string) => {
    if (!schema[section]) return undefined;

    return sub1 || selectedSub1 || Object.keys(schema[section]!)[0];
  };

  const selectSub2 = (section: string, sub1?: string, sub2?: string) => {
    const s1 = selectSub1(section, sub1);
    if (!s1) return undefined;
    if (!schema[section]![s1]) return undefined;

    return sub2 || selectedSub2 || schema[section]![s1]?.[0];
  };

  const select = (section: string, sub1?: string, sub2?: string) => () => {
    const s1 = selectSub1(section, sub1);
    const s2 = selectSub2(section, sub1, sub2);
    onSelect([section, s1, s2]);
    if (!sub2) toggleOpen();
  };

  const sub2 = () => {
    const sub1 = schema[selectedSection];
    if (!sub1) return undefined;
    if (!selectedSub1) return undefined;

    return sub1[selectedSub1];
  };

  const renderSideBar = () => (
    <SideBar>
      {Object.entries(schema).map(([section, subSections]) => (
        <Section key={section}>
          <Button
            selected={selectedSection === section}
            onClick={select(section)}
          >
            {translations ? formatMessage(translations[section]) : section}
          </Button>
          {!!subSections && (
            <Sub1
              className={classnames({
                selected: selectedSection === section,
              })}
            >
              {Object.keys(subSections).map(subSection => (
                <Button
                  key={`${section}/${subSection}`}
                  selected={selectedSub1 === subSection}
                  onClick={select(section, subSection)}
                >
                  {translations
                    ? formatMessage(translations[subSection])
                    : subSection}
                </Button>
              ))}
            </Sub1>
          )}
        </Section>
      ))}
    </SideBar>
  );

  const renderDropdown = () => (
    <Vertical>
      <Selected onClick={toggleOpen}>
        <BodyM bold>
          {selectedSection}
          {!!selectedSub1 && <>&nbsp;/&nbsp;{selectedSub1}</>}
        </BodyM>
        <FontAwesomeIcon icon={faChevronDown} />
      </Selected>
      {!!open && renderSideBar()}
    </Vertical>
  );

  return (
    <Root>
      {!isLaptop && renderDropdown()}
      <div>{title}</div>
      <Container>
        {!!isLaptop && renderSideBar()}
        <Content>
          {sub2() && (
            <Sub2>
              {sub2()?.map(sub2Title => (
                <TagButton
                  key={sub2Title}
                  onClick={select(selectedSection, selectedSub1, sub2Title)}
                  selected={sub2Title === selectedSub2}
                >
                  {sub2Title}
                </TagButton>
              ))}
            </Sub2>
          )}
          <div>{children}</div>
        </Content>
      </Container>
    </Root>
  );
};
